import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import computeMetrics from "../utils/cardMetrics";
import { apiUrl } from "../config/api";

function monthName(m) {
  const names = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const i = Math.max(1, Math.min(12, Number(m||0))) - 1;
  return names[i] || String(m);
}

// Форматирование чисел/процентов
const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function GroupSummary() {
  const { id } = useParams();
  const API_BASE = apiUrl();
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [error, setError] = useState("");
  const [groupInfo, setGroupInfo] = useState(null);
  const scrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  useEffect(() => { document.title = `Свод по группе ${id}`; }, [id]);
  useEffect(() => {
    if (groupInfo && groupInfo.name) {
      document.title = `Свод: ${groupInfo.name} (ID ${groupInfo.id})`;
    }
  }, [groupInfo]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const [sumRes, listRes, grp] = await Promise.all([
          fetch(`${API_BASE}/api/card-groups/${id}/summary`).then(r=>r.json()),
          fetch(`${API_BASE}/api/card-calc?full=1&perPage=1000`).then(r=>r.json()),
          fetch(`${API_BASE}/api/card-groups/${id}`).then(r=>r.json()).catch(()=>null),
        ]);
        const items = Array.isArray(listRes.items) ? listRes.items : [];
        const byId = new Map(items.map(c => [c.id, c]));
        const arr = (sumRes?.months || []).map(m => {
          const card = byId.get(m.cardId) || null;
          const metrics = card ? computeMetrics(card) : null;
          return { ...m, card, metrics };
        });
        if (mounted) {
          setMonths(arr);
          if (grp && grp.id) setGroupInfo(grp);
        }
      } catch (e) {
        if (mounted) setError('Ошибка загрузки свода');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id, API_BASE]);

  // Конфигурация строк в требуемом порядке
  const rows = useMemo(() => ([
    { key: 'contractTotal', title: 'Сумма контракта, руб.' },
    { key: 'profit_on_hand', title: 'Прибыль на руки, руб.' },
    { key: 'profit_on_hand_pct', title: 'Рентабельность чистая, %', isPercent: true },
    { key: 'fot_total', title: 'Общая сумма ФОТ (карта+нал)' },
    { key: 'fot_cash', title: 'Сумма ФОТ нал', triplet: ['fot_cash_sum', 'fot_cash_paid', 'fot_cash_remain'] },
    { key: 'fot_card', title: 'Сумма ФОТ на карту', triplet: ['fot_card_sum', 'fot_card_paid', 'fot_card_remain'] },
    { key: 'ndfl', title: 'НДФЛ, руб.' },
    { key: 'insurance', title: 'Страховые взносы' },
    { key: 'fot_tax', title: 'Сумма налогов на ФОТ, руб.' },
    { key: 'tmc_with_vat', title: 'ТМЦ (с НДС)', triplet: ['tmc_with_vat', 'tmc_paid_manual', 'tmc_remain'] },
    { key: 'amort_month', title: 'Амортизация оборудования и инвентаря' },
    { key: 'extras_vat', title: 'Доп.затраты, с НДС', triplet: ['extras_vat', 'extras_vat_paid', 'extras_vat_remain'] },
    { key: 'extras_no_vat', title: 'Доп.затраты, без НДС', triplet: ['extras_no_vat', 'extras_no_vat_paid', 'extras_no_vat_remain'] },
    { key: 'extras_cash', title: 'Доп.затраты, наличные', triplet: ['extras_cash', 'extras_cash_paid', 'extras_cash_remain'] },
    { key: 'vat_to_pay', title: 'НДС начисленный' },
    { key: 'vat_deduction', title: 'НДС в зачет' },
    { key: 'vat_delta', title: 'НДС на оплату' },
    { key: 'overhead', title: 'Накладные расходы' },
    { key: 'profit_tax', title: 'Налог на прибыль' },
    { key: 'cost_price', title: 'Себестоимость контракта' },
    { key: 'net_profit', title: 'Прибыль контракта' },
    { key: 'profitability_pct', title: 'Рентабельность, %', isPercent: true },
    { key: 'break_even', title: 'Точка безубыточности (справочно)' },
    { key: 'cash_remain_total', title: 'Итого осталось выплатить нал' },
    { key: 'subcontract_base', title: 'Субподряд На мыло (с НДС)' },
    { key: 'soap_profit_on_hand', title: 'Получено нал всего' },
    { key: 'vat_base_total', title: 'Чистая прибыль белая' },
    { key: 'base_total', title: 'ИТОГО затраты с налогами (без накладных)' },
    { key: 'soap_share_frac', title: 'Процент выручки минус затраты → мыло' },
  ]), []);

  const headers = useMemo(() => months.map(m => `${monthName(m.month)} ${m.year}`), [months]);
  const minTableWidth = useMemo(() => Math.max(900, 260 + 160 + months.length * 160), [months]);

  const syncScrollLeft = useCallback((source, target) => {
    if (!source || !target) return;
    const next = source.scrollLeft;
    if (Math.abs(target.scrollLeft - next) > 1) {
      target.scrollLeft = next;
    }
  }, []);

  const handleBodyScroll = useCallback(() => {
    syncScrollLeft(scrollRef.current, topScrollRef.current);
  }, [syncScrollLeft]);

  const handleTopScroll = useCallback(() => {
    syncScrollLeft(topScrollRef.current, scrollRef.current);
  }, [syncScrollLeft]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      const width = tableRef.current?.scrollWidth || 0;
      setTableScrollWidth(width);
      if (topScrollRef.current && scrollRef.current) {
        topScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [months, minTableWidth]);

  // ИТОГО по строкам
  const totals = useMemo(() => {
    const t = {};
    const sum = (arr, key) => arr.reduce((s, m) => s + (Number(m?.metrics?.[key]) || 0), 0);
    const weighted = (arr, key) => {
      let wsum = 0, w = 0;
      for (const m of arr) {
        const val = Number(m?.metrics?.[key]) || 0;
        const weight = Number(m?.metrics?.contractTotal) || 0;
        wsum += val * weight; w += weight;
      }
      return w > 0 ? (wsum / w) : 0;
    };
    for (const r of rows) {
      if (r.triplet) {
        const [a,b,c] = r.triplet;
        t[r.key] = {
          a: sum(months, a), b: sum(months, b), c: sum(months, c)
        };
      } else if (r.isPercent) {
        t[r.key] = weighted(months, r.key);
      } else {
        t[r.key] = sum(months, r.key);
      }
    }
    return t;
  }, [rows, months]);

  const TripletCell = ({ m, keys }) => {
    const sumK = keys?.[0];
    const leftK = keys?.[2];
    const sumV = m?.metrics?.[sumK];
    const leftV = m?.metrics?.[leftK];
    const bgLeft = (n) => {
      const r = Number(n) || 0;
      const eps = 0.005;
      if (r > eps) return '#ffe9d9';
      if (r < -eps) return '#fff3cf';
      return '#e8f7e5';
    };
    return (
      <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 2 }}>
        <div style={{ fontSize: 13.5, color: '#111827', fontWeight: 700 }}>∑ {fmtNum(sumV)}</div>
        <div style={{ fontSize: 12, background: bgLeft(leftV), padding: '1px 4px', borderRadius: 4, justifySelf: 'end' }}>ост. {fmtNum(leftV)}</div>
      </div>
    );
  };

  const TripletTotal = ({ rowKey }) => {
    const v = totals[rowKey] || { a: 0, b: 0, c: 0 };
    const bgLeft = (n) => {
      const r = Number(n) || 0;
      const eps = 0.005;
      if (r > eps) return '#ffe9d9';
      if (r < -eps) return '#fff3cf';
      return '#e8f7e5';
    };
    return (
      <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 2 }}>
        <div style={{ fontSize: 13.5, color: '#111827', fontWeight: 800 }}>∑ {fmtNum(v.a)}</div>
        <div style={{ fontSize: 12, background: bgLeft(v.c), padding: '1px 4px', borderRadius: 4, justifySelf: 'end', fontWeight: 700 }}>ост. {fmtNum(v.c)}</div>
      </div>
    );
  };

  const exportCsv = () => {
    const sep = ';';
    const header = ['Показатель','ИТОГО', ...headers];
    const lines = [header.join(sep)];
    const getVal = (m, r) => {
      if (r.triplet) {
        const a = r.triplet?.[0];
        const c = r.triplet?.[2];
        return `∑ ${fmtNum(m?.metrics?.[a])} / ост. ${fmtNum(m?.metrics?.[c])}`;
      }
      const v = m?.metrics?.[r.key];
      return fmtNum(v);
    };
    const getTotal = (r) => {
      if (r.triplet) {
        const tv = totals[r.key] || { a: 0, b: 0, c: 0 };
        return `∑ ${fmtNum(tv.a)} / ост. ${fmtNum(tv.c)}`;
      }
      return fmtNum(totals[r.key]);
    };
    for (const r of rows) {
      const row = [r.title, getTotal(r), ...months.map(m => getVal(m, r))];
      lines.push(row.join(sep));
    }
    const blob = new Blob(["\uFEFF" + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `group_${id}_summary.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Градиентная подсветка для прибыли на руки (руб/%)
  const profitStyle = (value, { isPercent = false } = {}) => {
    const v = Number(value) || 0;
    const abs = Math.abs(v);
    // Порог для насыщенности: для % — 30 п.п., для рублей — 1 млн
    const max = isPercent ? 30 : 1_000_000;
    const t = Math.max(0, Math.min(1, abs / max));
    const fill = Math.round(t * 100);
    const alpha = 0.12 + t * 0.38; // 0.12..0.50
    const pos = v > 0;
    const base = pos ? `rgba(34,197,94,${alpha})` : `rgba(239,68,68,${alpha})`; // tailwind green-500 / red-500 оттенки
    const text = pos ? '#065f46' : '#7f1d1d'; // тёмно-зелёный / тёмно-красный
    const border = pos ? '1px solid rgba(16,185,129,0.35)' : '1px solid rgba(239,68,68,0.35)';
    return {
      background: `linear-gradient(90deg, ${base} ${fill}%, rgba(0,0,0,0) ${fill}%)`,
      color: text,
      border,
      borderRadius: 6,
      padding: '2px 6px',
      display: 'inline-block',
      minWidth: 86,
      textAlign: 'right',
    };
  };

  return (
    <div style={{ padding: '32px 0 0 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Link to={`/groups/${id}`}>&larr; К группе</Link>
        <h2 style={{ margin: 0 }}>
          Сводная таблица группы #{id}
          {groupInfo?.name ? (
            <span style={{ fontWeight: 500, color: '#4b5563' }}> — {groupInfo.name}</span>
          ) : null}
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={exportCsv} style={{ padding: '8px 12px', background: '#e7f6d4', border: '1px solid #8bc34a', borderRadius: 6 }}>Выгрузить в Excel</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center' }}>Загрузка…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: '#c00' }}>{error}</div>
      ) : (
        <>
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              height: 14,
              margin: '6px 0',
              borderRadius: 40,
              background: '#e2e8f0',
              scrollbarGutter: 'stable'
            }}
            ref={topScrollRef}
            onScroll={handleTopScroll}
          >
            <div style={{ width: Math.max(tableScrollWidth, minTableWidth), height: 8 }} />
          </div>
          <div
            style={{ overflow: 'auto', maxHeight: 'calc(100vh - 140px)', position: 'relative', borderRadius: 18, background: '#fff', scrollbarGutter: 'stable' }}
            ref={scrollRef}
            onScroll={handleBodyScroll}
          >
            {/** Разметка и стили таблицы: сетка, зебра-строки и выделенный столбец ИТОГО */}
            <table
              ref={tableRef}
              style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              minWidth: minTableWidth,
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 10px rgba(0,0,0,0.06)'
            }}
          >
            <colgroup>
              <col style={{ width: 260 }} />
              <col style={{ width: 170 }} />
              {months.map((_, i) => (
                <col key={i} style={{ width: 150 }} />
              ))}
            </colgroup>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th
                  style={{
                    padding: 10,
                    textAlign: 'left',
                    borderBottom: '1px solid #e5e7eb',
                    borderRight: '1px solid #e5e7eb',
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    zIndex: 6,
                    background: '#f5f5f5',
                    boxShadow: '0 2px 0 rgba(0,0,0,0.04)'
                  }}
                ></th>
                <th
                  style={{
                    padding: 10,
                    textAlign: 'right',
                    borderBottom: '1px solid #e5e7eb',
                    borderRight: '1px solid #e5e7eb',
                    background: '#fff3cd',
                    fontWeight: 800,
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    boxShadow: '0 2px 0 rgba(0,0,0,0.04)'
                  }}
                >ИТОГО</th>
                {months.map((m, i) => (
                  <th
                    key={i}
                    style={{
                      padding: 10,
                      textAlign: 'center',
                      borderBottom: '1px solid #e5e7eb',
                      borderRight: i === months.length - 1 ? 'none' : '1px solid #e5e7eb',
                      position: 'sticky',
                      top: 0,
                      zIndex: 4,
                      background: '#f5f5f5',
                      boxShadow: '0 2px 0 rgba(0,0,0,0.04)'
                    }}
                  >
                    <div>{monthName(m.month)} {m.year}</div>
                    {m.card ? (
                      <div style={{ marginTop: 2, fontSize: 12 }}>
                        <a href={`/card-calc/${encodeURIComponent(m.card.objectCodeFull || m.card.objectCode)}`}
                           title={`Открыть карточку ${m.card.objectName || ''}`}
                           target="_blank" rel="noreferrer">
                          {m.card.objectCodeFull || String(m.card.objectCode).padStart(4,'0')}
                        </a>
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, rowIdx) => (
                <tr key={r.key} style={{ background: rowIdx % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                  <td
                    style={{
                      padding: 10,
                      textAlign: 'left',
                      fontWeight: 700,
                      borderTop: '1px solid #e5e7eb',
                      borderRight: '1px solid #e5e7eb',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      background: rowIdx % 2 === 0 ? '#ffffff' : '#fafafa',
                      boxShadow: '2px 0 0 rgba(0,0,0,0.04)'
                    }}
                  >{r.title}</td>
                  <td style={{ padding: 10, textAlign: 'right', verticalAlign: 'top', borderTop: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', background: '#fffdf4' }}>
                    {r.triplet ? (
                      <TripletTotal rowKey={r.key} />
                    ) : r.key === 'profit_on_hand' ? (
                      <span style={{ ...profitStyle(totals[r.key], { isPercent: false }), fontWeight: 700 }}>{fmtNum(totals[r.key])}</span>
                    ) : r.key === 'profit_on_hand_pct' ? (
                      <span style={{ ...profitStyle(totals[r.key], { isPercent: true }), fontWeight: 700 }}>{fmtNum(totals[r.key])}%</span>
                    ) : (
                      <span style={{ fontWeight: 700 }}>{r.isPercent ? fmtNum(totals[r.key]) : fmtNum(totals[r.key])}</span>
                    )}
                  </td>
                  {months.map((m, i) => (
                    <td key={i} style={{ padding: 10, textAlign: 'right', verticalAlign: 'top', borderTop: '1px solid #e5e7eb', borderRight: i === months.length - 1 ? 'none' : '1px solid #e5e7eb' }}>
                      {r.triplet ? (
                        <TripletCell m={m} keys={r.triplet} />
                      ) : r.key === 'profit_on_hand' ? (
                        <span style={profitStyle(m?.metrics?.[r.key], { isPercent: false })}>{fmtNum(m?.metrics?.[r.key])}</span>
                      ) : r.key === 'profit_on_hand_pct' ? (
                        <span style={profitStyle(m?.metrics?.[r.key], { isPercent: true })}>{fmtNum(m?.metrics?.[r.key])}%</span>
                      ) : (
                        fmtNum(m?.metrics?.[r.key])
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
